import { useEffect, useState, useMemo } from "react";
import { Plus, FileDown, Trash2, Loader2, Receipt, Calculator } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const empty = {
  date: new Date().toISOString().slice(0, 10),
  travel_from: "", travel_to: "", site_name: "", stay_location: "",
  num_days: 1, travel_expense: 0, food_expense: 0, room_expense: 0, other_expense: 0,
  remarks: "",
};

export default function ExpensesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/expenses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: e.target.value === "" ? 0 : parseFloat(e.target.value) });

  const total = useMemo(() =>
    (parseFloat(form.travel_expense) || 0) +
    (parseFloat(form.food_expense) || 0) +
    (parseFloat(form.room_expense) || 0) +
    (parseFloat(form.other_expense) || 0)
  , [form]);

  const openNew = () => { setForm(empty); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        num_days: parseFloat(form.num_days) || 1,
        travel_expense: parseFloat(form.travel_expense) || 0,
        food_expense: parseFloat(form.food_expense) || 0,
        room_expense: parseFloat(form.room_expense) || 0,
        other_expense: parseFloat(form.other_expense) || 0,
      };
      const { data } = await api.post("/expenses", payload);
      toast.success(`Expense ${data.expense_no} submitted — ₹${data.total_amount.toFixed(2)}`);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (exp) => {
    if (!window.confirm(`Delete ${exp.expense_no}?`)) return;
    try {
      await api.delete(`/expenses/${exp.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="expenses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Field Reports</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Receipt className="w-6 h-6 text-amber-500" /> My Expenses
          </h1>
        </div>
        <Button data-testid="new-expense-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Expense
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Expense No</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-left p-3 font-semibold">From → To</th>
              <th className="text-left p-3 font-semibold">Site</th>
              <th className="text-right p-3 font-semibold">Days</th>
              <th className="text-right p-3 font-semibold">Total (₹)</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} className="border-t border-slate-200">
                <td className="p-3 font-mono text-xs">{e.expense_no}</td>
                <td className="p-3 font-mono text-xs">{e.date}</td>
                <td className="p-3">{e.travel_from} → {e.travel_to}</td>
                <td className="p-3">{e.site_name}</td>
                <td className="p-3 text-right">{e.num_days}</td>
                <td className="p-3 text-right font-bold text-amber-700">₹ {e.total_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                <td className="p-3 text-right space-x-1">
                  {e.pdf_url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${e.pdf_url}`} target="_blank" rel="noopener noreferrer"
                       className="inline-flex p-2 hover:bg-slate-100" data-testid={`exp-pdf-${e.id}`}>
                      <FileDown className="w-4 h-4" />
                    </a>
                  )}
                  {user.role === "admin" && (
                    <button onClick={() => del(e)} className="p-2 hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No expense entries yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Submit New Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="expense-form">
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Date *</Label>
                <Input data-testid="exp-date" type="date" required value={form.date} onChange={setField("date")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Number of Days *</Label>
                <Input data-testid="exp-days" type="number" step="0.5" min="0.5" required value={form.num_days} onChange={setNum("num_days")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Site Name *</Label>
                <Input data-testid="exp-site" required value={form.site_name} onChange={setField("site_name")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Travel From *</Label>
                <Input data-testid="exp-from" required value={form.travel_from} onChange={setField("travel_from")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Travel To *</Label>
                <Input data-testid="exp-to" required value={form.travel_to} onChange={setField("travel_to")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Stay Location / Hotel</Label>
                <Input value={form.stay_location} onChange={setField("stay_location")} className="rounded-none" /></div>
            </div>

            <div className="grid md:grid-cols-4 gap-4 pt-2 border-t border-slate-200">
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Travel (₹)</Label>
                <Input data-testid="exp-travel" type="number" step="0.01" min="0" value={form.travel_expense} onChange={setNum("travel_expense")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Food (₹)</Label>
                <Input data-testid="exp-food" type="number" step="0.01" min="0" value={form.food_expense} onChange={setNum("food_expense")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Room (₹)</Label>
                <Input data-testid="exp-room" type="number" step="0.01" min="0" value={form.room_expense} onChange={setNum("room_expense")} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Other (₹)</Label>
                <Input data-testid="exp-other" type="number" step="0.01" min="0" value={form.other_expense} onChange={setNum("other_expense")} className="rounded-none" /></div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Remarks</Label>
              <Textarea rows={2} value={form.remarks} onChange={setField("remarks")} className="rounded-none" />
            </div>

            <div className="bg-amber-500 text-slate-950 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold">
                <Calculator className="w-5 h-5" /> TOTAL AMOUNT
              </div>
              <div className="font-heading font-black text-2xl" data-testid="exp-total">
                ₹ {total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button data-testid="exp-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit & Generate PDF
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
